// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, Button, Grid2, Stack, Typography } from '@mui/material';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
// Components, Layouts, Pages
import { BaseDatePicker, BaseInput, BaseSelect, Loading } from '~/components/common';
import { ItemInvoice } from '~/components/module/invoice';
// Others
import { IOrder, IOrderDetail, IParamQueryOrder, IParamQueryOrderDetail } from '~/utils/interface/order';
import { DiscountTypeEnum, TypeCrmEnum, TypeListInventory, TypeOrderDetailEnum } from '~/utils/enum/common';
import { formatCurrencyUSD, formatInputCurrency, getFullName } from '~/utils/helper/common';
import { getListOrder, getOrderDetail } from '~/thunks/order/orderThunk';
import { IFormInvoice, IInvoiceItems } from '~/utils/interface/invoice';
import { getListClient } from '~/thunks/client/clientThunk';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IClient, IOptionClient, IQueryParamClient } from '~/utils/interface/client';
import { IBaseOption, IPayloadCallApiFn, IResultCallApiFn } from '~/utils/interface/common';
import {
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
  DOLLAR,
  EMPTY_STRING,
  optionPaymentTerms,
} from '~/utils/constants/common';
// Styles, images, icons
import styles from './InfoInvoiceForm.module.scss';
import { icons } from '~/assets';

type Props = {
  clientNameDefault?: string;
  orderNameDefault?: string;
  customerInfo?: IOptionClient;
  setCustomerInfo?: Dispatch<SetStateAction<IOptionClient | undefined>>;
};

const cx = classNames.bind(styles);

const InfoInvoiceForm = (props: Props) => {
  //#region Destructuring Props
  const { clientNameDefault, orderNameDefault, customerInfo, setCustomerInfo } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/invoice', 'common/base']);
  const dispatch = useAppDispatch();

  const {
    control,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useFormContext<IFormInvoice>();

  const { orderId, invoiceItems, discountRate, taxAmount, balanceDue } = watch();

  const {
    fields: invoiceItemsFields,
    append: appendInvoiceItems,
    remove: removeInvoiceItems,
  } = useFieldArray({
    control,
    name: 'invoiceItems',
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (orderId) return;

    const totalItem = invoiceItems?.reduce((total, item) => {
      return Number(total || 0) + Number(item?.total || 0);
    }, 0);

    setValue('subtotal', totalItem?.toFixed(2).toString());

    const total = Number(totalItem || 0) + Number(taxAmount || 0) - Number(discountRate || 0);

    setValue('total', Math.max(total, 0).toFixed(2).toString());
  }, [JSON.stringify(invoiceItems), discountRate, taxAmount]);

  useEffect(() => {
    if (!orderId) return;

    const newTotal = Number(balanceDue || 0) + Number(taxAmount || 0) - Number(discountRate || 0);

    setValue('total', Math.max(newTotal, 0).toFixed(2).toString());
  }, [discountRate, taxAmount]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetOrderDetail = (orderId?: string) => {
    if (!orderId) return;

    const params: IParamQueryOrderDetail = {
      orderId: orderId || EMPTY_STRING,
      type: TypeOrderDetailEnum.OVERVIEW,
    };

    setIsLoading(true);

    dispatch(getOrderDetail(params))
      .unwrap()
      .then((res) => {
        const { data } = res;

        const detailOrder = data as IOrderDetail;

        const orderItems = [
          ...detailOrder?.orderItems.filter((item) => item.type !== TypeListInventory.PACKAGE && !item?.isDelete),
          ...detailOrder?.orderAddOns.filter((item) => !item?.isDelete),
        ];

        const newItems: IInvoiceItems[] = orderItems.map((item) => {
          return {
            productId: `${item.inventoryId}_${item.variantId}`,
            inventoryId: item?.inventoryId || EMPTY_STRING,
            variantId: item?.variantId || EMPTY_STRING,
            name: item?.name || EMPTY_STRING,
            variantName: item?.variantName || EMPTY_STRING,
            quantity: item?.quantity.toString() || EMPTY_STRING,
            laborCost: item?.laborCost || EMPTY_STRING,
            price: item?.price.toString() || EMPTY_STRING,
            discountType: item?.discountType || EMPTY_STRING,
            discountRate: item?.discountRate || EMPTY_STRING,
            total: item?.total.toString() || EMPTY_STRING,
          };
        });

        setValue('invoiceItems', newItems);
        setValue('subtotal', detailOrder?.subtotal || EMPTY_STRING);
        setValue('taxRate', detailOrder?.taxRate || EMPTY_STRING);
        setValue('taxAmount', EMPTY_STRING);
        setValue('discountRate', EMPTY_STRING);
        setValue('total', detailOrder?.amountDue || EMPTY_STRING);
        setValue('amountPaid', detailOrder?.amountPaid || EMPTY_STRING);
        setValue('balanceDue', detailOrder?.amountDue || EMPTY_STRING);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchOptionsClient = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IOptionClient>> => {
      const { searchKey, page } = payload;
      const payloadParams: IQueryParamClient = {
        page: page,
        limit: DEFAULT_LIMIT_PAGE,
        type: TypeCrmEnum.CLIENT,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListClient(payloadParams)).unwrap();

        const { responses: listClient, pagination } = resp?.data;

        const formatOptions: IOptionClient[] = listClient?.map((item: IClient) => ({
          value: item?.id,
          label:
            item?.firstName || item?.lastName
              ? getFullName({ firstName: item?.firstName, lastName: item?.lastName })
              : item?.entityName,
          email: item?.clientContacts?.[0]?.email || EMPTY_STRING,
        }));

        const hasMore: boolean = pagination?.page < pagination?.totalPages || false;

        return { optionsResp: formatOptions, hasMoreResp: hasMore };
      } catch (error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    []
  );

  const fetchOptionsOrder = useCallback(async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
    const { page, searchKey } = payload;
    const payloadParams: IParamQueryOrder = {
      page: page,
      limit: DEFAULT_LIMIT_PAGE,
      ...(searchKey && { textSearch: searchKey }),
    };

    try {
      const resp = await dispatch(getListOrder(payloadParams)).unwrap();

      const { responses: listOrder, pagination } = resp?.data;

      const formatOptions: IBaseOption[] = listOrder?.map((item: IOrder) => ({
        value: item?.id,
        label: item?.orderNumber || EMPTY_STRING,
      }));

      const hasMore: boolean = pagination?.page < pagination?.totalPages || false;

      return { optionsResp: formatOptions, hasMoreResp: hasMore };
    } catch (error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);

  const fetchOptionsPaymentTerm = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionPaymentTerms,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const handleAddItem = () => {
    const newItem: IInvoiceItems = {
      productId: EMPTY_STRING,
      inventoryId: EMPTY_STRING,
      variantId: EMPTY_STRING,
      name: EMPTY_STRING,
      variantName: EMPTY_STRING,
      quantity: EMPTY_STRING,
      laborCost: EMPTY_STRING,
      price: EMPTY_STRING,
      discountType: DiscountTypeEnum.AMOUNT,
      discountRate: EMPTY_STRING,
      total: EMPTY_STRING,
    };

    appendInvoiceItems(newItem);
  };

  const handleChangeCustomer = (data: IOptionClient | null) => {
    if (!data) return;

    setCustomerInfo && setCustomerInfo(data);
  };
  //#endregion Handle Function

  return (
    <Stack id='infoInventoryFormComponent' gap={5}>
      <Grid2 container size={12} spacing={6}>
        <Grid2 container size={9} spacing={6} className={cx('viewLeft')}>
          <Box className={cx('viewCustomer')}>
            <Typography className={cx('title')}>{t('invoice_form_title_customer_information')}</Typography>

            <Stack direction={'column'} spacing={4}>
              <Controller
                control={control}
                name={'clientId'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseSelect
                      isSearch
                      gap={10}
                      required
                      height={36}
                      width={'50%'}
                      styleType='contained'
                      label={t('invoice_form_title_customer')}
                      callApiFn={fetchOptionsClient}
                      type='loadMore'
                      value={value}
                      onChange={(data) => {
                        onChange(data?.value);
                        handleChangeCustomer(data);
                      }}
                      itemNameDefault={clientNameDefault}
                      errorMessage={errors?.clientId?.message}
                    />
                  );
                }}
              />

              {customerInfo && (
                <Box className={cx('viewInfoCustomer')}>
                  <Stack direction={'row'} spacing={2}>
                    <Avatar />

                    <Stack direction={'column'}>
                      <Typography className={cx('fullNameCustomer')}>{customerInfo?.label || EMPTY_STRING}</Typography>

                      <Typography className={cx('emailCustomer')}>{customerInfo?.email || EMPTY_STRING}</Typography>
                    </Stack>
                  </Stack>
                </Box>
              )}
            </Stack>
          </Box>

          <Box className={cx('viewCustomer')}>
            <Typography className={cx('title')}>{t('invoice_form_title_line_items')}</Typography>

            {invoiceItemsFields?.map((itemInvoice, index) => {
              return (
                <ItemInvoice
                  disabled={!!orderId}
                  key={itemInvoice.id ?? index}
                  index={index}
                  onRemove={(indexDelete) => removeInvoiceItems(indexDelete)}
                />
              );
            })}

            {!orderId && (
              <Button
                type='button'
                variant='contained'
                className={cx('buttonAdd')}
                onClick={handleAddItem}
                startIcon={
                  <img
                    className={cx('iconAdd')}
                    src={icons.commonIconPlusPrimary}
                    alt={t('common_alt_img', { ns: 'common/base' })}
                  />
                }
              >
                {t('common_text_add_item', { ns: 'common/base' })}
              </Button>
            )}
          </Box>

          <Box className={cx('viewCustomer')}>
            <Typography className={cx('title')}>{t('invoice_form_title_notes_terms')}</Typography>

            <Controller
              control={control}
              name={'note'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    multiline
                    rows={7}
                    height={'auto'}
                    label={t('invoice_form_title_note')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.note?.message}
                  />
                );
              }}
            />
          </Box>
        </Grid2>

        <Grid2 container size={3} spacing={6} className={cx('viewRight')}>
          <Box className={cx('viewInvoiceSummary')}>
            <Typography className={cx('title')}>{t('invoice_form_title_invoice_summary')}</Typography>
            <Controller
              control={control}
              name={'orderId'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseSelect
                    isSearch
                    gap={6}
                    height={36}
                    styleType='contained'
                    label={t('invoice_form_title_order')}
                    callApiFn={fetchOptionsOrder}
                    type='loadMore'
                    value={value}
                    onChange={(data) => {
                      onChange(data?.value);
                      handleGetOrderDetail(data?.value);
                    }}
                    itemNameDefault={orderNameDefault}
                    errorMessage={errors?.orderId?.message}
                  />
                );
              }}
            />

            <Controller
              control={control}
              name={'paymentTerms'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseSelect
                    gap={10}
                    height={36}
                    styleType='contained'
                    label={t('invoice_form_title_payment_terms')}
                    callApiFn={fetchOptionsPaymentTerm}
                    value={value}
                    onChange={(data) => {
                      onChange(data?.value);
                    }}
                    errorMessage={errors?.paymentTerms?.message}
                  />
                );
              }}
            />

            <Controller
              control={control}
              name={'dueDate'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseDatePicker
                    mode='date'
                    required
                    height={36}
                    label={t('invoice_form_title_due_date')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors.dueDate?.message}
                  />
                );
              }}
            />
          </Box>

          <Box className={cx('viewInvoiceSummary')}>
            <Typography className={cx('title')}>{t('invoice_form_title_summary')}</Typography>

            <Controller
              control={control}
              name={'discountRate'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    startAdornment={<Typography className={cx('startAdornment')}>{DOLLAR}</Typography>}
                    label={t('invoice_form_title_discount')}
                    value={value}
                    onChange={(e) => {
                      onChange(formatInputCurrency(e.target.value));
                    }}
                    errorMessage={errors?.discountRate?.message}
                  />
                );
              }}
            />

            <Controller
              control={control}
              name={'taxAmount'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    startAdornment={<Typography className={cx('startAdornment')}>{DOLLAR}</Typography>}
                    label={t('invoice_form_title_tax_amount')}
                    value={value}
                    onChange={(e) => {
                      onChange(formatInputCurrency(e.target.value));
                    }}
                    errorMessage={errors?.taxAmount?.message}
                  />
                );
              }}
            />
            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
              <Typography className={cx('emailCustomer')}>{t('invoice_form_title_subtotal')}</Typography>

              <Typography className={cx('emailCustomer')}>
                {formatCurrencyUSD({ value: getValues('subtotal'), showDecimal: true })}
              </Typography>
            </Stack>

            {orderId && (
              <>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                  <Typography className={cx('emailCustomer')}>{t('invoice_form_amount_paid')}</Typography>

                  <Typography className={cx('emailCustomer')}>
                    {formatCurrencyUSD({ value: getValues('amountPaid'), showDecimal: true })}
                  </Typography>
                </Stack>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                  <Typography className={cx('emailCustomer')}>{t('invoice_form_amount_due')}</Typography>

                  <Typography className={cx('emailCustomer')}>
                    {formatCurrencyUSD({ value: getValues('balanceDue'), showDecimal: true })}
                  </Typography>
                </Stack>
              </>
            )}

            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
              <Typography className={cx('emailCustomer')}>{t('invoice_form_title_tax')}</Typography>

              <Typography className={cx('emailCustomer')}>
                {formatCurrencyUSD({ value: getValues('taxAmount'), showDecimal: true })}
              </Typography>
            </Stack>

            <Stack className={cx('viewTotal')}>
              <Typography className={cx('title')}>{t('invoice_form_title_total')}</Typography>

              <Typography className={cx('title')}>
                {formatCurrencyUSD({ value: getValues('total'), showDecimal: true })}
              </Typography>
            </Stack>
          </Box>
        </Grid2>
      </Grid2>

      {isLoading && <Loading />}
    </Stack>
  );
};

export default InfoInvoiceForm;
